import { useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSegment, useEditHash, useMutateSegment } from 'crm-index-visualization-core';
import { clearProcessedLogs } from './slice';
import { reduceActionsToSegment } from './segmentReducer';
import { RESET_VIEW } from '../../views/actions/viewsActionTypes';
import Raven from 'raven-js';

/**
 * Component that handles synchronization between Redux state and viz-core
 */
export const VizCoreBridge = () => {
  var _segmentRef$current, _segmentRef$current2, _segmentRef$current3, _segmentRef$current4, _segmentRef$current5, _segmentRef$current6;
  const dispatch = useDispatch();
  const {
    segmentSyncLog
  } = useSelector(state => state.vizCoreBridge);
  const {
    data: segment,
    loading: segmentLoading
  } = useSegment();
  const segmentRef = useRef(segment);
  const segmentIsDefined = useMemo(() => {
    return Boolean(segment);
  }, [segment]);

  // Update the ref when segment changes
  useEffect(() => {
    segmentRef.current = segment;
  }, [segment]);
  const [saveSegment] = useMutateSegment({
    onError(e) {
      Raven.captureException(e, {
        extra: {
          location: 'VizCoreBridge'
        }
      });
    }
  });
  const [editHash, setEditHash] = useEditHash();

  // Synchronize Redux changes to viz-core
  useEffect(() => {
    if (segmentSyncLog.length > 0 && segmentRef.current && segmentIsDefined && !segmentLoading) {
      const logsToProcess = [...segmentSyncLog];

      // Check if we have a RESET_VIEW action
      const resetViewIndex = logsToProcess.findLastIndex(action => action.type === RESET_VIEW);
      const hasResetViewAction = resetViewIndex !== -1;

      // If there's a RESET_VIEW action, clear the edit hash
      if (hasResetViewAction) {
        setEditHash(null);
      }

      // If we had a RESET_VIEW action, only process actions that came after the last RESET_VIEW
      const actionsToProcess = hasResetViewAction ? logsToProcess.slice(resetViewIndex + 1) : logsToProcess;

      // If there are no actions to process after filtering, clear the processed logs and exit
      if (actionsToProcess.length === 0 && hasResetViewAction) {
        dispatch(clearProcessedLogs(logsToProcess.length));
        return;
      }

      // Reduce the logs to a final segment state
      const updatedSegment = reduceActionsToSegment(segmentRef.current, actionsToProcess);

      // Save to viz-core
      saveSegment(updatedSegment).then(() => {
        dispatch(clearProcessedLogs(logsToProcess.length));
      }).catch(error => {
        // Logs will remain in the queue and be retried on next sync cycle
        console.error('Failed to sync segment to viz-core:', error);
      });
    }
  }, [segmentSyncLog, dispatch, saveSegment, setEditHash, segmentIsDefined, segmentLoading]);

  // Debug view - remove in production
  const __ = {
    hasCurrentSegment: !!segmentRef.current,
    segmentSyncLogLength: segmentSyncLog.length,
    currentSegmentSearch: (_segmentRef$current = segmentRef.current) === null || _segmentRef$current === void 0 ? void 0 : _segmentRef$current.search,
    currentSegmentPipelineId: (_segmentRef$current2 = segmentRef.current) === null || _segmentRef$current2 === void 0 ? void 0 : _segmentRef$current2.pipelineId,
    currentSegmentSorts: (_segmentRef$current3 = segmentRef.current) === null || _segmentRef$current3 === void 0 ? void 0 : _segmentRef$current3.sorts,
    currentSegmentQuickFilters: (_segmentRef$current4 = segmentRef.current) === null || _segmentRef$current4 === void 0 ? void 0 : _segmentRef$current4.quickFilters,
    currentSegmentQuickFilterProperties: (_segmentRef$current5 = segmentRef.current) === null || _segmentRef$current5 === void 0 ? void 0 : _segmentRef$current5.quickFilterProperties,
    currentSegmentAdvancedFilterGroups: (_segmentRef$current6 = segmentRef.current) === null || _segmentRef$current6 === void 0 ? void 0 : _segmentRef$current6.advancedFilterGroups,
    editHash
  };
  return null;
};